import React, { useEffect, useRef, useState } from "react";
import {
  TextField,
  Grid,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Radio,
  FormLabel,
  Switch,
  Slider,
  IconButton,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import ImageIcon from "@mui/icons-material/Image";
import AddIcon from "@mui/icons-material/Add";
import FolderIcon from "@mui/icons-material/Folder";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import QuizIcon from "@mui/icons-material/Quiz";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import Chip from "@mui/material/Chip";
import Swal from "sweetalert2";
import {
  createCurso,
  createSeccion,
  uploadCursoPortada,
  getPreguntasSugeridas,
} from "../services/cursosService";
import { createPregunta } from "../services/preguntasService";

const TIPO_LABELS = {
  multiple: "Selección múltiple",
  única: "Una sola respuesta",
  falso_verdadero: "Verdadero / Falso",
  autocompletar: "Autocompletar",
  rango: "Rango",
};

const PORTADA_MAX_BYTES = 5 * 1024 * 1024;
const PORTADA_MIME = ["image/jpeg", "image/png", "image/webp", "image/gif"];

const CreateCourseForm = ({ onSuccess }) => {
  const [step, setStep] = useState(1);
  const [idCurso, setIdCurso] = useState(null);
  const [formData, setFormData] = useState({
    Nombre: "",
    Descripcion: "",
    Estado: 1,
    PorcentajeAprobacion: 70,
    CantidadPreguntas: 10,
  });

  const [blockingMessage, setBlockingMessage] = useState("");

  const [preview, setPreview] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const previewObjectUrlRef = useRef("");

  const revokePreviewUrl = () => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = "";
    }
  };

  useEffect(() => () => revokePreviewUrl(), []);

  // Paso 2: Secciones (nombre, orden, estado, archivo PDF)
  const [seccionForm, setSeccionForm] = useState({
    nombre: "",
    orden: 1,
    estado: 1,
    archivo: null,
  });
  const [seccionFileName, setSeccionFileName] = useState("");
  const [secciones, setSecciones] = useState([]);
  const [loadingSection, setLoadingSection] = useState(false);

  // Paso 3: Registro manual de preguntas
  const [preguntasCurso, setPreguntasCurso] = useState([]);
  const [guardandoPreguntas, setGuardandoPreguntas] = useState(false);

  // Paso 3: Sugerencias de preguntas por IA
  const [sugerencias, setSugerencias] = useState([]);
  const [cargandoSugerencias, setCargandoSugerencias] = useState(false);
  const [preguntaForm, setPreguntaForm] = useState({
    pregunta: "",
    descripcion: "",
    tipo: "multiple",
    opciones: [
      { respuesta: "", correcta: false },
      { respuesta: "", correcta: false },
    ],
    valorVerdaderoFalso: true,
    respuestasAutocompletar: [""],
    rango: [0, 100],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    const parsedValue =
      name === "Estado" || name === "PorcentajeAprobacion" || name === "CantidadPreguntas"
        ? parseInt(value, 10)
        : value;
    setFormData((prev) => ({
      ...prev,
      [name]: parsedValue,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!PORTADA_MIME.includes(file.type)) {
      Swal.fire("Error", "Formato no permitido. Usa JPEG, PNG, WebP o GIF.", "error");
      e.target.value = "";
      return;
    }
    if (file.size > PORTADA_MAX_BYTES) {
      Swal.fire("Error", "La imagen supera el máximo permitido (5 MB).", "error");
      e.target.value = "";
      return;
    }
    revokePreviewUrl();
    const objectUrl = URL.createObjectURL(file);
    previewObjectUrlRef.current = objectUrl;
    setCoverFile(file);
    setFileName(file.name);
    setPreview(objectUrl);
    e.target.value = "";
  };

  const handleSubmitCourse = async (e) => {
    e?.preventDefault?.();

    if (!formData.Nombre?.trim()) {
      return Swal.fire("Error", "El nombre del curso es obligatorio", "error");
    }

    if (!formData.Descripcion?.trim()) {
      return Swal.fire("Error", "La descripción del curso es obligatoria", "error");
    }
    if (!Number.isInteger(formData.PorcentajeAprobacion) || formData.PorcentajeAprobacion < 1 || formData.PorcentajeAprobacion > 100) {
      return Swal.fire("Error", "El porcentaje de aprobación debe estar entre 1 y 100.", "error");
    }
    if (!Number.isInteger(formData.CantidadPreguntas) || formData.CantidadPreguntas < 1 || formData.CantidadPreguntas > 25) {
      return Swal.fire("Error", "La cantidad de preguntas debe estar entre 1 y 25.", "error");
    }

    try {
      setLoading(true);
      setBlockingMessage("Creando curso...");

      const payload = {
        Nombre: formData.Nombre.trim(),
        Descripcion: formData.Descripcion.trim(),
        Estado: formData.Estado,
        PorcentajeAprobacion: formData.PorcentajeAprobacion,
        CantidadPreguntas: formData.CantidadPreguntas,
      };

      const response = await createCurso(payload);

      if (response?.success !== false) {
        const cursoId = response?.idCurso ?? response?.id ?? response?.data?.idCurso ?? response?.data?.id;
        if (cursoId != null) {
          if (coverFile) {
            try {
              setBlockingMessage("Subiendo imagen de portada...");
              await uploadCursoPortada(cursoId, coverFile);
            } catch (uploadError) {
              console.error("Error al subir portada:", uploadError);
              const msg =
                uploadError?.response?.data?.message ||
                uploadError?.message ||
                "El curso se creó pero no se pudo subir la imagen de portada.";
              Swal.fire("Advertencia", msg, "warning");
            }
          }
          setIdCurso(cursoId);
          setStep(2);
          Swal.fire({
            icon: "success",
            title: "Curso creado",
            text: "Ahora puedes agregar las secciones del curso.",
          });
        } else {
          Swal.fire("Advertencia", "Curso creado pero no se obtuvo el ID. Revisa la respuesta del servidor.", "warning");
          onSuccess?.();
        }
      } else {
        Swal.fire(
          "Error",
          response?.message || "Error al crear el curso",
          "error"
        );
      }
    } catch (error) {
      console.error("Error al crear curso:", error);
      const data = error?.response?.data;
      const message =
        data?.messages?.error || data?.message || error?.message || "Error en la conexión con el servidor";
      Swal.fire("Error", message, "error");
    } finally {
      setLoading(false);
      setBlockingMessage("");
    }
  };

  const handleSeccionFormChange = (e) => {
    const { name, value } = e.target;
    const parsed = name === "orden" ? parseInt(value, 10) || 1 : name === "estado" ? parseInt(value, 10) : value;
    setSeccionForm((prev) => ({ ...prev, [name]: parsed }));
  };

  const handleSeccionFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== "application/pdf") {
        Swal.fire("Error", "Solo se permiten archivos PDF.", "error");
        return;
      }
      setSeccionForm((prev) => ({ ...prev, archivo: file }));
      setSeccionFileName(file.name);
    }
    e.target.value = "";
  };

  const clearSeccionForm = () => {
    setSeccionForm({ nombre: "", orden: secciones.length + 1, estado: 1, archivo: null });
    setSeccionFileName("");
  };

  const handleAddSection = async (e) => {
    e?.preventDefault?.();

    if (!seccionForm.nombre?.trim()) {
      return Swal.fire("Error", "El nombre de la sección es obligatorio.", "error");
    }
    if (!seccionForm.archivo) {
      return Swal.fire("Error", "Debes seleccionar un archivo PDF para la sección.", "error");
    }
    if (!idCurso) return;

    try {
      setLoadingSection(true);
      setBlockingMessage("Agregando sección...");
      const response = await createSeccion(idCurso, {
        nombre: seccionForm.nombre.trim(),
        orden: seccionForm.orden,
        estado: seccionForm.estado,
        archivo: seccionForm.archivo,
      });

      if (response?.success !== false) {
        Swal.fire({
          icon: "success",
          title: "Sección agregada",
          text: response?.message || "Sección creada correctamente.",
        });
        setSecciones((prev) => [
          ...prev,
          {
            nombre: seccionForm.nombre.trim(),
            orden: seccionForm.orden,
            estado: seccionForm.estado,
            archivoNombre: seccionForm.archivo.name,
          },
        ]);
        clearSeccionForm();
        setSeccionForm((prev) => ({ ...prev, orden: secciones.length + 2 }));
      } else {
        const msg = response?.messages?.error || response?.message || "Error al crear la sección";
        Swal.fire("Error", msg, "error");
      }
    } catch (error) {
      console.error("Error al crear sección:", error);
      const data = error?.response?.data;
      const msg =
        data?.messages?.error || data?.message || error?.message || "Error en la conexión con el servidor";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoadingSection(false);
      setBlockingMessage("");
    }
  };

  const handleGoToStep3 = () => {
    setStep(3);
  };

  const handleSugerirPreguntas = async () => {
    if (!idCurso) return;

    try {
      setCargandoSugerencias(true);
      setBlockingMessage("Generando preguntas sugeridas con IA...");
      const response = await getPreguntasSugeridas(idCurso);
      const preguntasIA = Array.isArray(response?.preguntas) ? response.preguntas : [];

      if (preguntasIA.length === 0) {
        Swal.fire("Sin sugerencias", "La IA no devolvió preguntas. Verifica que las secciones tengan PDFs con texto legible.", "info");
        return;
      }

      setSugerencias(
        preguntasIA.map((p) => ({
          pregunta: p.pregunta || "",
          descripcion: p.descripcion || "",
          tipo: p.tipo || "única",
          opciones: Array.isArray(p.opciones) ? p.opciones.map((o) => ({ respuesta: o.respuesta || "", correcta: !!o.correcta })) : [],
        }))
      );
      Swal.fire({
        icon: "success",
        title: "Sugerencias generadas",
        text: `Se generaron ${preguntasIA.length} preguntas sugeridas. Revísalas antes de agregarlas.`,
      });
    } catch (error) {
      console.error("Error al obtener preguntas sugeridas:", error);
      const msg =
        error?.response?.data?.message || error?.message || "No se pudieron generar preguntas sugeridas.";
      Swal.fire("Error", msg, "error");
    } finally {
      setCargandoSugerencias(false);
      setBlockingMessage("");
    }
  };

  const handleDescartarSugerencia = (index) => {
    setSugerencias((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUsarSugerenciaTalCual = (index) => {
    const sugerencia = sugerencias[index];
    if (!sugerencia) return;

    if (preguntasCurso.length >= formData.CantidadPreguntas) {
      Swal.fire("Aviso", `Ya registraste las ${formData.CantidadPreguntas} preguntas requeridas.`, "info");
      return;
    }

    setPreguntasCurso((prev) => [
      ...prev,
      {
        pregunta: sugerencia.pregunta.trim(),
        descripcion: sugerencia.descripcion.trim(),
        tipo: sugerencia.tipo,
        rango: [0, 100],
        estado: 1,
        opciones: sugerencia.opciones,
      },
    ]);
    handleDescartarSugerencia(index);
  };

  const handleEditarSugerencia = (index) => {
    const sugerencia = sugerencias[index];
    if (!sugerencia) return;

    if (sugerencia.tipo === "falso_verdadero") {
      const correctaVerdadero = sugerencia.opciones.find((o) => o.respuesta === "Verdadero")?.correcta;
      setPreguntaForm({
        pregunta: sugerencia.pregunta,
        descripcion: sugerencia.descripcion,
        tipo: "falso_verdadero",
        opciones: [],
        valorVerdaderoFalso: correctaVerdadero !== false,
        respuestasAutocompletar: [""],
        rango: [0, 100],
      });
    } else if (sugerencia.tipo === "autocompletar") {
      const respuestas = sugerencia.opciones.length > 0 ? sugerencia.opciones.map((o) => o.respuesta) : [""];
      setPreguntaForm({
        pregunta: sugerencia.pregunta,
        descripcion: sugerencia.descripcion,
        tipo: "autocompletar",
        opciones: [],
        valorVerdaderoFalso: true,
        respuestasAutocompletar: respuestas,
        rango: [0, 100],
      });
    } else {
      const opciones =
        sugerencia.opciones.length >= 2
          ? sugerencia.opciones
          : [
              { respuesta: "", correcta: false },
              { respuesta: "", correcta: false },
            ];
      setPreguntaForm({
        pregunta: sugerencia.pregunta,
        descripcion: sugerencia.descripcion,
        tipo: sugerencia.tipo === "multiple" ? "multiple" : "única",
        opciones,
        valorVerdaderoFalso: true,
        respuestasAutocompletar: [""],
        rango: [0, 100],
      });
    }

    handleDescartarSugerencia(index);
  };

  const resetPreguntaForm = () => {
    setPreguntaForm({
      pregunta: "",
      descripcion: "",
      tipo: "multiple",
      opciones: [
        { respuesta: "", correcta: false },
        { respuesta: "", correcta: false },
      ],
      valorVerdaderoFalso: true,
      respuestasAutocompletar: [""],
      rango: [0, 100],
    });
  };

  const handlePreguntaFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === "tipo") {
      if (value === "autocompletar") {
        setPreguntaForm((prev) => ({
          ...prev,
          tipo: value,
          opciones: [],
          respuestasAutocompletar: [""],
        }));
        return;
      }

      if (value === "falso_verdadero") {
        setPreguntaForm((prev) => ({
          ...prev,
          tipo: value,
          opciones: [],
        }));
        return;
      }

      if (value === "rango") {
        setPreguntaForm((prev) => ({
          ...prev,
          tipo: value,
          opciones: [],
        }));
        return;
      }

      setPreguntaForm((prev) => ({
        ...prev,
        tipo: value,
        opciones:
          prev.opciones.length >= 2
            ? prev.opciones
            : [
                { respuesta: "", correcta: false },
                { respuesta: "", correcta: false },
              ],
      }));
      return;
    }

    if (name === "pregunta") {
      const blanks = (value.match(/_____/g) || []).length;
      setPreguntaForm((prev) => {
        if (prev.tipo !== "autocompletar") {
          return { ...prev, pregunta: value };
        }
        const respuestas = [...prev.respuestasAutocompletar];
        while (respuestas.length < blanks) respuestas.push("");
        return {
          ...prev,
          pregunta: value,
          respuestasAutocompletar: respuestas.slice(0, blanks || 1),
        };
      });
      return;
    }

    setPreguntaForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddOpcionPregunta = () => {
    setPreguntaForm((prev) => ({
      ...prev,
      opciones: [...prev.opciones, { respuesta: "", correcta: false }],
    }));
  };

  const handleOpcionPreguntaChange = (index, field, value) => {
    setPreguntaForm((prev) => {
      const updated = [...prev.opciones];
      updated[index][field] = value;
      if (prev.tipo === "única" && field === "correcta" && value) {
        updated.forEach((op, i) => {
          if (i !== index) op.correcta = false;
        });
      }
      return { ...prev, opciones: updated };
    });
  };

  const handleRemoveOpcionPregunta = (index) => {
    setPreguntaForm((prev) => ({
      ...prev,
      opciones: prev.opciones.filter((_, i) => i !== index),
    }));
  };

  const handleRespuestaAutocompletarChange = (index, value) => {
    setPreguntaForm((prev) => {
      const updated = [...prev.respuestasAutocompletar];
      updated[index] = value;
      return { ...prev, respuestasAutocompletar: updated };
    });
  };

  const buildOpcionesPregunta = () => {
    if (preguntaForm.tipo === "rango") {
      return [];
    }

    if (preguntaForm.tipo === "falso_verdadero") {
      return [
        { respuesta: "Verdadero", correcta: preguntaForm.valorVerdaderoFalso === true },
        { respuesta: "Falso", correcta: preguntaForm.valorVerdaderoFalso === false },
      ];
    }

    if (preguntaForm.tipo === "autocompletar") {
      return preguntaForm.respuestasAutocompletar.map((respuesta) => ({
        respuesta: respuesta.trim(),
        correcta: true,
      }));
    }

    return preguntaForm.opciones.map((op) => ({
      respuesta: op.respuesta.trim(),
      correcta: !!op.correcta,
    }));
  };

  const validarPreguntaActual = () => {
    if (!preguntaForm.pregunta.trim()) {
      Swal.fire("Error", "La pregunta no puede estar vacía.", "error");
      return false;
    }

    if (preguntaForm.tipo === "autocompletar") {
      const blanks = (preguntaForm.pregunta.match(/_____/g) || []).length;
      if (blanks === 0) {
        Swal.fire("Error", "Para autocompletar debes incluir al menos un espacio (_____).", "error");
        return false;
      }
      const vacias = preguntaForm.respuestasAutocompletar.filter((r) => !r.trim());
      if (vacias.length > 0) {
        Swal.fire("Error", "Completa todas las respuestas de autocompletar.", "error");
        return false;
      }
      return true;
    }

    if (preguntaForm.tipo === "rango") {
      if (preguntaForm.rango[0] >= preguntaForm.rango[1]) {
        Swal.fire("Error", "El rango debe tener un minimo menor al maximo.", "error");
        return false;
      }
      return true;
    }

    if (preguntaForm.tipo === "única" || preguntaForm.tipo === "multiple") {
      if (preguntaForm.opciones.length < 2) {
        Swal.fire("Error", "Debe haber al menos 2 opciones.", "error");
        return false;
      }
      if (preguntaForm.opciones.some((op) => !op.respuesta.trim())) {
        Swal.fire("Error", "Ninguna opción puede estar vacía.", "error");
        return false;
      }
      const correctas = preguntaForm.opciones.filter((op) => op.correcta).length;
      if (preguntaForm.tipo === "única" && correctas !== 1) {
        Swal.fire("Error", "Debe haber exactamente una opción correcta.", "error");
        return false;
      }
      if (preguntaForm.tipo === "multiple" && correctas < 1) {
        Swal.fire("Error", "Debe haber al menos una opción correcta.", "error");
        return false;
      }
    }

    return true;
  };

  const handleAgregarPreguntaCurso = () => {
    if (preguntasCurso.length >= formData.CantidadPreguntas) {
      Swal.fire("Aviso", `Ya registraste las ${formData.CantidadPreguntas} preguntas requeridas.`, "info");
      return;
    }
    if (!validarPreguntaActual()) return;

    const nuevaPregunta = {
      pregunta: preguntaForm.pregunta.trim(),
      descripcion: preguntaForm.descripcion.trim(),
      tipo: preguntaForm.tipo,
      rango: preguntaForm.rango,
      estado: 1,
      opciones: buildOpcionesPregunta(),
    };

    setPreguntasCurso((prev) => [...prev, nuevaPregunta]);
    resetPreguntaForm();
  };

  const handleEliminarPreguntaCurso = (index) => {
    setPreguntasCurso((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFinish = () => {
    if (step === 3 && preguntasCurso.length !== formData.CantidadPreguntas) {
      Swal.fire("Error", `Debes registrar exactamente ${formData.CantidadPreguntas} preguntas para finalizar.`, "error");
      return;
    }
    Swal.fire({
      icon: "success",
      title: "Proceso completado",
      text: "Curso, secciones y preguntas configurados correctamente.",
    });
    setStep(1);
    setIdCurso(null);
    setFormData({ Nombre: "", Descripcion: "", Estado: 1, PorcentajeAprobacion: 70, CantidadPreguntas: 10 });
    revokePreviewUrl();
    setCoverFile(null);
    setPreview("");
    setFileName("");
    setSecciones([]);
    setPreguntasCurso([]);
    setSugerencias([]);
    resetPreguntaForm();
    clearSeccionForm();
    onSuccess?.();
  };

  const handleBackToStep1 = () => {
    setStep(1);
    setIdCurso(null);
    setFormData({ Nombre: "", Descripcion: "", Estado: 1, PorcentajeAprobacion: 70, CantidadPreguntas: 10 });
    revokePreviewUrl();
    setCoverFile(null);
    setPreview("");
    setFileName("");
    setSecciones([]);
    setPreguntasCurso([]);
    setSugerencias([]);
    resetPreguntaForm();
    clearSeccionForm();
  };

  const steps = ["Datos del curso", "Secciones del curso", "Preguntas manuales"];

  const handleGuardarPreguntasYFinalizar = async () => {
    if (preguntasCurso.length !== formData.CantidadPreguntas) {
      Swal.fire("Error", `Debes registrar exactamente ${formData.CantidadPreguntas} preguntas para finalizar.`, "error");
      return;
    }
    if (!idCurso) {
      Swal.fire("Error", "No se encontró el ID del curso para guardar las preguntas.", "error");
      return;
    }

    try {
      setGuardandoPreguntas(true);
      setBlockingMessage("Guardando preguntas...");
      await Promise.all(
        preguntasCurso.map((pregunta) =>
          createPregunta({
            ...pregunta,
            idCurso: Number(idCurso),
          })
        )
      );
      handleFinish();
    } catch (error) {
      console.error("Error al guardar preguntas del curso:", error);
      const msg = error?.response?.data?.message || error?.message || "No se pudieron guardar las preguntas.";
      Swal.fire("Error", msg, "error");
    } finally {
      setGuardandoPreguntas(false);
      setBlockingMessage("");
    }
  };

  return (
    <>
      <Backdrop
        open={!!blockingMessage}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 1,
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="body1">{blockingMessage}</Typography>
      </Backdrop>
      <Card sx={{ backgroundColor: "#fff", borderRadius: 2, boxShadow: "none" }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <SchoolIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Crear nuevo curso</Typography>
        </Box>

        <Stepper activeStep={step - 1} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Paso 1: Crear curso */}
        {step === 1 && (
          <Box component="form" onSubmit={handleSubmitCourse} sx={{ width: "100%" }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nombre del curso"
                  name="Nombre"
                  value={formData.Nombre}
                  onChange={handleChange}
                  required
                  placeholder="Ej: Introducción a Polímeros"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción"
                  name="Descripcion"
                  value={formData.Descripcion}
                  onChange={handleChange}
                  required
                  multiline
                  rows={4}
                  placeholder="Curso introductorio..."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Porcentaje de aprobación (%)"
                  name="PorcentajeAprobacion"
                  type="number"
                  inputProps={{ min: 1, max: 100 }}
                  value={formData.PorcentajeAprobacion}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cantidad de preguntas"
                  name="CantidadPreguntas"
                  type="number"
                  inputProps={{ min: 1, max: 25 }}
                  value={formData.CantidadPreguntas}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    border: "1px dashed #ccc",
                    borderRadius: 2,
                    textAlign: "center",
                    bgcolor: "#fafafa",
                  }}
                >
                  {preview ? (
                    <Box>
                      <Box
                        component="img"
                        src={preview}
                        alt="Vista previa"
                        sx={{
                          maxHeight: 160,
                          maxWidth: "100%",
                          objectFit: "contain",
                          borderRadius: 1,
                          mb: 1,
                        }}
                      />
                      <Typography variant="caption" display="block">
                        {fileName}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => {
                          revokePreviewUrl();
                          setCoverFile(null);
                          setPreview("");
                          setFileName("");
                        }}
                        sx={{ mt: 1 }}
                      >
                        Quitar imagen
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <ImageIcon sx={{ fontSize: 48, color: "action.disabled", mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Imagen de portada
                      </Typography>
                      <input
                        accept="image/*"
                        id="upload-cover"
                        type="file"
                        style={{ display: "none" }}
                        onChange={handleImageChange}
                      />
                      <label htmlFor="upload-cover">
                        <Button variant="outlined" component="span">
                          Seleccionar imagen
                        </Button>
                      </label>
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="estado-label">Estado</InputLabel>
                  <Select
                    labelId="estado-label"
                    name="Estado"
                    value={formData.Estado}
                    label="Estado"
                    onChange={handleChange}
                  >
                    <MenuItem value={1}>Activo</MenuItem>
                    <MenuItem value={0}>Inactivo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box mt={2} display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setFormData({
                        Nombre: "",
                        Descripcion: "",
                        Estado: 1,
                        PorcentajeAprobacion: 70,
                        CantidadPreguntas: 10,
                      });
                      revokePreviewUrl();
                      setCoverFile(null);
                      setPreview("");
                      setFileName("");
                    }}
                  >
                    Limpiar
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitCourse}
                    disabled={loading}
                  >
                    {loading ? "Creando..." : "Crear curso y continuar"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Paso 2: Agregar secciones (nombre, orden, estado, PDF) */}
        {step === 2 && (
          <Box sx={{ width: "100%" }}>
            <Box display="flex" alignItems="center" mb={2}>
              <FolderIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle1">
                Agregar secciones al curso
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleAddSection} mb={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre de la sección"
                    name="nombre"
                    value={seccionForm.nombre}
                    onChange={handleSeccionFormChange}
                    placeholder="Ej: Introducción"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Orden"
                    name="orden"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={seccionForm.orden}
                    onChange={handleSeccionFormChange}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel id="seccion-estado-label">Estado</InputLabel>
                    <Select
                      labelId="seccion-estado-label"
                      name="estado"
                      value={seccionForm.estado}
                      label="Estado"
                      onChange={handleSeccionFormChange}
                    >
                      <MenuItem value={1}>Activo</MenuItem>
                      <MenuItem value={0}>Inactivo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.5,
                      border: "1px dashed #ccc",
                      borderRadius: 2,
                      bgcolor: "#fafafa",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Archivo PDF de la sección
                    </Typography>
                    <input
                      accept=".pdf,application/pdf"
                      id="seccion-pdf"
                      type="file"
                      style={{ display: "none" }}
                      onChange={handleSeccionFileChange}
                    />
                    <label htmlFor="seccion-pdf">
                      <Button variant="outlined" component="span" startIcon={<PictureAsPdfIcon />} size="small">
                        {seccionFileName || "Seleccionar PDF"}
                      </Button>
                    </label>
                    {seccionFileName && (
                      <Button size="small" onClick={() => { setSeccionForm((p) => ({ ...p, archivo: null })); setSeccionFileName(""); }} sx={{ ml: 1 }}>
                        Quitar
                      </Button>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddSection}
                    disabled={loadingSection}
                  >
                    {loadingSection ? "Agregando..." : "Agregar sección"}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {secciones.length > 0 && (
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Secciones agregadas ({secciones.length})
                </Typography>
                <List dense sx={{ bgcolor: "#fafafa", borderRadius: 1 }}>
                  {secciones.map((sec, idx) => (
                    <ListItem key={idx}>
                      <ListItemText
                        primary={sec.nombre}
                        secondary={`Orden: ${sec.orden} · Estado: ${sec.estado === 1 ? "Activo" : "Inactivo"} · ${sec.archivoNombre}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            <Box display="flex" gap={2} justifyContent="space-between" flexWrap="wrap">
              <Button variant="outlined" onClick={handleBackToStep1} fullWidth sx={{ maxWidth: { xs: "100%", sm: "fit-content" } }}>
                Crear otro curso
              </Button>
              <Box display="flex" gap={1} flexWrap="wrap" width={{ xs: "100%", sm: "auto" }}>
                <Button variant="outlined" onClick={handleFinish} fullWidth={true} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  Omitir y finalizar
                </Button>
                <Button variant="contained" color="primary" onClick={handleGoToStep3} fullWidth={true} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  Continuar a preguntas
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Paso 3: Preguntas manuales del curso */}
        {step === 3 && (
          <Box sx={{ width: "100%" }}>
            <Box display="flex" alignItems="center" mb={2}>
              <QuizIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle1">
                Registrar preguntas del curso ({preguntasCurso.length}/{formData.CantidadPreguntas})
              </Typography>
            </Box>

            <Box
              sx={{
                p: 2,
                mb: 3,
                border: "1px dashed #ccc",
                borderRadius: 2,
                bgcolor: "#fafafa",
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={sugerencias.length > 0 ? 2 : 0}>
                <Typography variant="body2" color="text.secondary">
                  Genera preguntas sugeridas por IA a partir del contenido de los PDFs subidos en las secciones.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleSugerirPreguntas}
                  disabled={cargandoSugerencias}
                >
                  {cargandoSugerencias ? "Generando sugerencias..." : "Sugerir preguntas con IA"}
                </Button>
              </Box>

              {sugerencias.length > 0 && (
                <List dense sx={{ bgcolor: "#fff", borderRadius: 1 }}>
                  {sugerencias.map((sug, idx) => (
                    <ListItem
                      key={`sugerencia-${idx}`}
                      divider={idx < sugerencias.length - 1}
                      secondaryAction={
                        <Box display="flex" gap={0.5}>
                          <IconButton
                            title="Editar antes de agregar"
                            onClick={() => handleEditarSugerencia(idx)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            title="Usar tal cual"
                            color="primary"
                            onClick={() => handleUsarSugerenciaTalCual(idx)}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton title="Descartar" onClick={() => handleDescartarSugerencia(idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={sug.pregunta}
                        secondary={
                          <>
                            <Chip label={TIPO_LABELS[sug.tipo] || sug.tipo} size="small" sx={{ mr: 1 }} />
                            {sug.opciones.length > 0 ? `${sug.opciones.length} opciones` : "Sin opciones"}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  name="tipo"
                  label="Tipo de pregunta"
                  value={preguntaForm.tipo}
                  onChange={handlePreguntaFieldChange}
                >
                  <MenuItem value="multiple">Selección múltiple</MenuItem>
                  <MenuItem value="única">Una sola respuesta</MenuItem>
                  <MenuItem value="falso_verdadero">Verdadero / Falso</MenuItem>
                  <MenuItem value="autocompletar">Autocompletar</MenuItem>
                  <MenuItem value="rango">Rango</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Descripción (opcional)"
                  name="descripcion"
                  value={preguntaForm.descripcion}
                  onChange={handlePreguntaFieldChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Pregunta"
                  name="pregunta"
                  value={preguntaForm.pregunta}
                  onChange={handlePreguntaFieldChange}
                  placeholder={
                    preguntaForm.tipo === "autocompletar"
                      ? "Ej: El plástico _____ puede reciclarse fácilmente."
                      : ""
                  }
                />
              </Grid>
              {(preguntaForm.tipo === "única" || preguntaForm.tipo === "multiple") &&
                preguntaForm.opciones.map((opcion, index) => (
                  <Grid item xs={12} key={`opcion-${index}`}>
                    <Box display="flex" alignItems={{ xs: "stretch", sm: "center" }} gap={1} flexWrap={{ xs: "wrap", sm: "nowrap" }}>
                      {preguntaForm.tipo === "única" ? (
                        <Radio
                          checked={opcion.correcta}
                          onChange={() => handleOpcionPreguntaChange(index, "correcta", true)}
                        />
                      ) : (
                        <Checkbox
                          checked={opcion.correcta}
                          onChange={(e) => handleOpcionPreguntaChange(index, "correcta", e.target.checked)}
                        />
                      )}
                      <TextField
                        fullWidth
                        label={`Opción ${index + 1}`}
                        value={opcion.respuesta}
                        onChange={(e) => handleOpcionPreguntaChange(index, "respuesta", e.target.value)}
                      />
                      <IconButton
                        onClick={() => handleRemoveOpcionPregunta(index)}
                        disabled={preguntaForm.opciones.length <= 2}
                        sx={{ alignSelf: { xs: "flex-end", sm: "center" } }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              {(preguntaForm.tipo === "única" || preguntaForm.tipo === "multiple") && (
                <Grid item xs={12}>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddOpcionPregunta}>
                    Agregar opción
                  </Button>
                </Grid>
              )}
              {preguntaForm.tipo === "falso_verdadero" && (
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FormLabel>Respuesta correcta:</FormLabel>
                    <Switch
                      checked={preguntaForm.valorVerdaderoFalso}
                      onChange={(e) =>
                        setPreguntaForm((prev) => ({
                          ...prev,
                          valorVerdaderoFalso: e.target.checked,
                        }))
                      }
                    />
                    <Typography variant="body2">
                      {preguntaForm.valorVerdaderoFalso ? "Verdadero" : "Falso"}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {preguntaForm.tipo === "autocompletar" &&
                preguntaForm.respuestasAutocompletar.map((respuesta, index) => (
                  <Grid item xs={12} key={`auto-${index}`}>
                    <TextField
                      fullWidth
                      label={`Respuesta ${index + 1}`}
                      value={respuesta}
                      onChange={(e) => handleRespuestaAutocompletarChange(index, e.target.value)}
                    />
                  </Grid>
                ))}
              <Grid item xs={12}>
                <FormLabel sx={{ mb: 1, display: "block" }}>Rango de edad permitido</FormLabel>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Selecciona la edad minima y maxima recomendada para esta pregunta.
                </Typography>
                <Slider
                  value={preguntaForm.rango}
                  onChange={(_, newValue) => setPreguntaForm((prev) => ({ ...prev, rango: newValue }))}
                  valueLabelDisplay="auto"
                  min={0}
                  max={100}
                  step={1}
                  disableSwap
                />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="caption">Min: {preguntaForm.rango[0]} anos</Typography>
                  <Typography variant="caption">Max: {preguntaForm.rango[1]} anos</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleAgregarPreguntaCurso}
                  disabled={preguntasCurso.length >= formData.CantidadPreguntas}
                >
                  Agregar pregunta al curso
                </Button>
              </Grid>
            </Grid>

            {preguntasCurso.length > 0 && (
              <List dense sx={{ bgcolor: "#fafafa", borderRadius: 1, mb: 3 }}>
                {preguntasCurso.map((item, idx) => (
                  <ListItem
                    key={`pregunta-${idx}`}
                    secondaryAction={
                      <IconButton edge="end" onClick={() => handleEliminarPreguntaCurso(idx)}>
                        <DeleteIcon />
                      </IconButton>
                    }
                    divider={idx < preguntasCurso.length - 1}
                  >
                    <ListItemText
                      primary={`${idx + 1}. ${item.pregunta}`}
                      secondary={`Tipo: ${item.tipo} · Opciones: ${item.opciones.length}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            <Box display="flex" gap={2} justifyContent="flex-end" flexWrap="wrap">
              <Button variant="outlined" onClick={() => setStep(2)} fullWidth sx={{ width: { xs: "100%", sm: "auto" } }}>
                Volver a secciones
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGuardarPreguntasYFinalizar}
                disabled={guardandoPreguntas || preguntasCurso.length !== formData.CantidadPreguntas}
                fullWidth
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {guardandoPreguntas ? "Guardando preguntas..." : `Guardar ${formData.CantidadPreguntas} preguntas y finalizar`}
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
      </Card>
    </>
  );
};

export default CreateCourseForm;
